<template>
  <div class="flex h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 font-sans text-white overflow-hidden">
    <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&family=Inter:wght@400;500;600;700;800&family=Outfit:wght@400;500;600;700;800;900&family=Space+Grotesk:wght@400;500;600;700&family=Orbitron:wght@400;500;600;700;800;900&display=swap" rel="stylesheet">

    <!-- SIDEBAR GAUCHE - Sections -->
    <aside class="w-1/6 bg-slate-900/50 backdrop-blur-xl border-r border-slate-700/50 flex flex-col z-20 shadow-2xl">
      <div class="p-8 border-b border-slate-700/50">
        <div class="flex items-center gap-3 mb-2">
          <div class="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/20">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
          </div>
          <div>
            <h1 class="text-2xl font-black tracking-tight bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">Designer</h1>
            <p class="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Écran d'affichage</p>
          </div>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto p-6 space-y-6">
        <!-- Sections de personnalisation -->
        <div v-for="section in sections" :key="section.id"
             @click="activeSection = section.id"
             :class="['group p-5 rounded-2xl cursor-pointer transition-all border-2', 
                      activeSection === section.id 
                        ? 'bg-blue-500/20 border-blue-500/50 shadow-lg shadow-blue-500/10' 
                        : 'bg-slate-800/30 border-slate-700/30 hover:bg-slate-800/50 hover:border-slate-600/50']">
          <div class="flex items-center gap-4">
            <div :class="['w-10 h-10 rounded-xl flex items-center justify-center text-lg transition-all',
                          activeSection === section.id ? 'bg-blue-500 shadow-lg shadow-blue-500/30' : 'bg-slate-700/50']">
              {{ section.icon }}
            </div>
            <div class="flex-1">
              <h3 class="font-bold text-sm">{{ section.name }}</h3>
              <p class="text-[10px] text-slate-400">{{ section.desc }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="p-6 border-t border-slate-700/50 space-y-3">
        <button @click="saveConfig" class="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-2xl font-black shadow-xl shadow-blue-600/20 transition-all flex items-center justify-center gap-2 active:scale-95">
          <span>🚀 Publier l'écran</span>
        </button>
        <button @click="resetToDefault" class="w-full py-3 bg-slate-800/50 hover:bg-slate-700/50 text-slate-300 rounded-xl font-bold text-sm transition-all">
          Réinitialiser
        </button>
      </div>
    </aside>

    <!-- ZONE CENTRALE - Preview -->
    <main class=" w-4/6 flex-1 flex items-center justify-center p-12 relative overflow-hidden">
      <!-- Grille de fond -->
      <div class="absolute inset-0 opacity-5" style="background-image: radial-gradient(circle, white 1px, transparent 1px); background-size: 20px 20px;"></div>
      
      <div class="relative">
        <!-- Frame TV -->
        <div class="relative bg-slate-950 p-8 rounded-[3rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.8)] border-[12px] border-slate-800">
          <!-- Preview de l'écran TV (16:9) -->
          <div 
            id="tv-screen"
            class="relative overflow-hidden bg-slate-900 rounded-[2rem] shadow-2xl"
            style="width: 960px; height: 540px;"
            :style="getScreenBackgroundStyle()"
          >
            <!-- Header -->
            <div 
              v-if="config.layout.showHeader"
              :class="['px-8 flex justify-between items-center border-b backdrop-blur-md relative z-10', config.layout.headerPosition === 'top' ? 'order-1' : 'order-3']"
              :style="{
                height: config.layout.headerHeight + 'px',
                backgroundColor: config.header.backgroundColor + (config.header.opacity * 100).toString(16).padStart(2, '0'),
                borderColor: config.header.borderColor
              }"
            >
              <div class="flex items-center gap-4">
                <h1 
                  class="font-black tracking-tighter uppercase"
                  :style="{
                    fontSize: config.header.titleSize + 'px',
                    color: config.header.titleColor,
                    fontFamily: config.header.titleFont
                  }"
                >
                  {{ config.header.title }}
                </h1>
              </div>
              
              <div v-if="config.layout.showClock" class="flex flex-col items-end gap-1">
                <span 
                  class="font-black uppercase tracking-widest"
                  :style="{
                    fontSize: config.header.subtitleSize + 'px',
                    color: config.header.subtitleColor,
                    fontFamily: config.header.subtitleFont
                  }"
                >
                  HEURE LOCALE
                </span>
                <div 
                  class="font-mono font-black tabular-nums leading-none"
                  :style="{
                    fontSize: config.header.clockSize + 'px',
                    color: config.header.clockColor,
                    fontFamily: config.header.clockFont
                  }"
                >
                  14:35
                </div>
              </div>
            </div>

            <!-- Main Content Area -->
            <div 
              class="flex order-2"
              :class="config.layout.orientation === 'horizontal' ? 'flex-row' : 'flex-col'"
              :style="{ 
                height: config.layout.showHeader && config.layout.showFooter 
                  ? `calc(540px - ${config.layout.headerHeight}px - ${config.layout.footerHeight}px)` 
                  : config.layout.showHeader 
                    ? `calc(540px - ${config.layout.headerHeight}px)` 
                    : config.layout.showFooter 
                      ? `calc(540px - ${config.layout.footerHeight}px)` 
                      : '540px'
              }"
            >
              <!-- Zone d'appel principale -->
              <div 
                v-if="config.layout.showCalling"
                class="flex flex-col items-center justify-center p-8 relative"
                :style="{ 
                  backgroundColor: config.calling.backgroundColor + (config.calling.opacity * 100).toString(16).padStart(2, '0'),
                  width: config.layout.orientation === 'horizontal' ? config.layout.callingWidth + '%' : '100%',
                  height: config.layout.orientation === 'vertical' ? config.layout.callingHeight + '%' : 'auto',
                  order: config.layout.callingPosition === 'left' || config.layout.callingPosition === 'top' ? 1 : 2
                }"
              >
                <div class="text-center">
                  <span 
                    class="font-black uppercase tracking-widest mb-4 block"
                    :style="{
                      fontSize: config.calling.labelSize + 'px',
                      color: config.calling.labelColor,
                      fontFamily: config.calling.labelFont
                    }"
                  >
                    {{ config.calling.label }}
                  </span>
                  <div 
                    class="font-black leading-none mb-6"
                    :style="{
                      fontSize: config.calling.numberSize + 'px',
                      color: config.calling.numberColor,
                      fontFamily: config.calling.numberFont,
                      textShadow: '0 20px 20px rgba(0,0,0,0.3)'
                    }"
                  >
                    A042
                  </div>
                  <div 
                    class="px-10 py-4 rounded-3xl font-black uppercase shadow-2xl"
                    :style="{
                      backgroundColor: config.calling.counterBg,
                      color: config.calling.counterColor,
                      fontSize: config.calling.counterSize + 'px',
                      fontFamily: config.calling.counterFont,
                      borderRadius: config.calling.counterRadius + 'px'
                    }"
                  >
                    {{ config.calling.counterText }}
                  </div>
                </div>
              </div>

              <!-- Sidebar historique -->
              <div 
                v-if="config.layout.showHistory"
                class="backdrop-blur-sm p-6"
                :class="config.layout.orientation === 'horizontal' ? 'border-l' : 'border-t'"
                :style="{
                  backgroundColor: config.history.backgroundColor + (config.history.opacity * 100).toString(16).padStart(2, '0'),
                  borderColor: config.history.borderColor,
                  width: config.layout.orientation === 'horizontal' ? (100 - config.layout.callingWidth) + '%' : '100%',
                  height: config.layout.orientation === 'vertical' ? (100 - config.layout.callingHeight) + '%' : 'auto',
                  order: config.layout.callingPosition === 'left' || config.layout.callingPosition === 'top' ? 2 : 1
                }"
              >
                <h2 
                  class="font-black uppercase tracking-widest mb-6 flex items-center gap-3"
                  :style="{
                    fontSize: config.history.titleSize + 'px',
                    color: config.history.titleColor,
                    fontFamily: config.history.titleFont
                  }"
                >
                  <span class="h-px flex-1" :style="{ backgroundColor: config.history.titleColor }"></span>
                  {{ config.history.title }}
                  <span class="h-px flex-1" :style="{ backgroundColor: config.history.titleColor }"></span>
                </h2>
                
                <div class="space-y-3">
                  <div v-for="i in 3" :key="i" 
                       class="flex justify-between items-center p-4 rounded-2xl border"
                       :style="{
                         backgroundColor: config.history.itemBg,
                         borderColor: config.history.itemBorderColor,
                         borderRadius: config.history.itemRadius + 'px'
                       }">
                    <div class="flex flex-col">
                      <div 
                        class="font-black mb-1"
                        :style="{
                          fontSize: config.history.ticketSize + 'px',
                          color: config.history.ticketColor,
                          fontFamily: config.history.ticketFont
                        }"
                      >
                        A{{ String(40 + i).padStart(3, '0') }}
                      </div>
                      <div 
                        class="text-xs font-black uppercase tracking-widest"
                        :style="{ 
                          color: config.history.queueColor,
                          fontFamily: config.history.queueFont
                        }"
                      >
                        Service
                      </div>
                    </div>
                    <div 
                      class="px-4 py-2 rounded-xl font-black"
                      :style="{
                        backgroundColor: config.history.counterBg,
                        color: config.history.counterColor,
                        fontSize: config.history.counterSize + 'px'
                      }"
                    >
                      Guichet {{ i }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div 
              v-if="config.layout.showFooter"
              :class="['border-t flex items-center px-8 overflow-hidden', config.layout.headerPosition === 'top' ? 'order-3' : 'order-1']"
              :style="{
                height: config.layout.footerHeight + 'px',
                backgroundColor: config.footer.backgroundColor + (config.footer.opacity * 100).toString(16).padStart(2, '0'),
                borderColor: config.footer.borderColor
              }"
            >
              <div class="flex gap-8 animate-scroll-slow whitespace-nowrap">
                <div v-for="i in 4" :key="i" class="flex items-center gap-3">
                  <span 
                    class="w-2 h-2 rounded-full"
                    :style="{ backgroundColor: config.footer.dotColor }"
                  ></span>
                  <span 
                    class="font-black uppercase tracking-widest"
                    :style="{
                      fontSize: config.footer.labelSize + 'px',
                      color: config.footer.labelColor,
                      fontFamily: config.footer.labelFont
                    }"
                  >
                    File {{ i }}:
                  </span>
                  <span 
                    class="font-black"
                    :style="{
                      fontSize: config.footer.countSize + 'px',
                      color: config.footer.countColor,
                      fontFamily: config.footer.countFont
                    }"
                  >
                    {{ 5 + i }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Info responsive -->
        <div class="absolute -bottom-8 left-0 right-0 text-center">
          <span class="text-xs text-slate-500 font-mono">960×540 (16:9 TV Display)</span>
        </div>
      </div>
    </main>

    <!-- SIDEBAR DROITE - Propriétés -->
    <aside class="w-1/6 bg-slate-900/50 backdrop-blur-xl border-l border-slate-700/50 flex flex-col z-20 shadow-2xl overflow-hidden">
      <div class="p-6 border-b border-slate-700/50 bg-gradient-to-r from-blue-500/10 to-indigo-500/10">
        <h2 class="font-black text-sm uppercase tracking-wider flex items-center gap-2">
          <span class="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
          Personnalisation
        </h2>
      </div>

      <div class="flex-1 overflow-y-auto p-6">
        <!-- LAYOUT SECTION -->
        <div v-if="activeSection === 'layout'" class="space-y-6">
          <h3 class="text-lg font-black uppercase tracking-wide text-blue-400 mb-4">Disposition</h3>
          
          <div class="space-y-6">
            <!-- Visibilité des sections -->
            <div class="bg-slate-800/30 p-4 rounded-xl space-y-3">
              <h4 class="text-xs font-bold text-slate-400 uppercase mb-3">Sections visibles</h4>
              
              <label class="flex items-center justify-between cursor-pointer">
                <span class="text-sm font-medium">En-tête</span>
                <input type="checkbox" v-model="config.layout.showHeader" class="w-5 h-5 rounded accent-blue-500" />
              </label>
              
              <label class="flex items-center justify-between cursor-pointer">
                <span class="text-sm font-medium">Horloge</span>
                <input type="checkbox" v-model="config.layout.showClock" :disabled="!config.layout.showHeader" class="w-5 h-5 rounded accent-blue-500" />
              </label>
              
              <label class="flex items-center justify-between cursor-pointer">
                <span class="text-sm font-medium">Zone d'appel</span>
                <input type="checkbox" v-model="config.layout.showCalling" class="w-5 h-5 rounded accent-blue-500" />
              </label>
              
              <label class="flex items-center justify-between cursor-pointer">
                <span class="text-sm font-medium">Historique</span>
                <input type="checkbox" v-model="config.layout.showHistory" class="w-5 h-5 rounded accent-blue-500" />
              </label>
              
              <label class="flex items-center justify-between cursor-pointer">
                <span class="text-sm font-medium">Pied de page</span>
                <input type="checkbox" v-model="config.layout.showFooter" class="w-5 h-5 rounded accent-blue-500" />
              </label>
            </div>

            <!-- Orientation du contenu -->
            <div v-if="config.layout.showCalling && config.layout.showHistory">
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Orientation du contenu</label>
              <select v-model="config.layout.orientation" class="w-full p-3 bg-slate-800 border border-slate-700 rounded-xl text-sm font-medium focus:border-blue-500 outline-none transition-all">
                <option value="horizontal">Horizontal (côte à côte)</option>
                <option value="vertical">Vertical (empilé)</option>
              </select>
            </div>

            <!-- Position de l'en-tête -->
            <div v-if="config.layout.showHeader">
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Position de l'en-tête</label>
              <select v-model="config.layout.headerPosition" class="w-full p-3 bg-slate-800 border border-slate-700 rounded-xl text-sm">
                <option value="top">En haut</option>
                <option value="bottom">En bas</option>
              </select>
            </div>

            <!-- Hauteur de l'en-tête -->
            <div v-if="config.layout.showHeader">
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Hauteur de l'en-tête (px)</label>
              <div class="flex items-center gap-4">
                <input type="range" v-model.number="config.layout.headerHeight" min="60" max="120" class="flex-1" />
                <span class="text-sm font-bold bg-slate-800 px-3 py-1 rounded-lg w-16 text-center">{{ config.layout.headerHeight }}</span>
              </div>
            </div>

            <!-- Hauteur du footer -->
            <div v-if="config.layout.showFooter">
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Hauteur du pied de page (px)</label>
              <div class="flex items-center gap-4">
                <input type="range" v-model.number="config.layout.footerHeight" min="60" max="120" class="flex-1" />
                <span class="text-sm font-bold bg-slate-800 px-3 py-1 rounded-lg w-16 text-center">{{ config.layout.footerHeight }}</span>
              </div>
            </div>

            <!-- Position de la zone d'appel -->
            <div v-if="config.layout.showCalling && config.layout.showHistory">
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Position zone d'appel</label>
              <select v-model="config.layout.callingPosition" class="w-full p-3 bg-slate-800 border border-slate-700 rounded-xl text-sm">
                <option v-if="config.layout.orientation === 'horizontal'" value="left">À gauche</option>
                <option v-if="config.layout.orientation === 'horizontal'" value="right">À droite</option>
                <option v-if="config.layout.orientation === 'vertical'" value="top">En haut</option>
                <option v-if="config.layout.orientation === 'vertical'" value="bottom">En bas</option>
              </select>
            </div>

            <!-- Largeur/Hauteur de la zone d'appel -->
            <div v-if="config.layout.showCalling && config.layout.showHistory">
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">
                {{ config.layout.orientation === 'horizontal' ? 'Largeur zone d\'appel (%)' : 'Hauteur zone d\'appel (%)' }}
              </label>
              <div class="flex items-center gap-4">
<input
  type="range"
  v-model.number="callingSize"
  min="40"
  max="80"
  class="flex-1"
/>

<span class="text-sm font-bold bg-slate-800 px-3 py-1 rounded-lg w-16 text-center">
  {{ callingSize }}%
</span>

<p class="text-xs text-slate-500 mt-2">
  Historique: {{ 100 - callingSize }}%
</p>

                <span class="text-sm font-bold bg-slate-800 px-3 py-1 rounded-lg w-16 text-center">
                  {{ config.layout.orientation === 'horizontal' ? config.layout.callingWidth : config.layout.callingHeight }}%
                </span>
              </div>
              <p class="text-xs text-slate-500 mt-2">
                Historique: {{ config.layout.orientation === 'horizontal' ? (100 - config.layout.callingWidth) : (100 - config.layout.callingHeight) }}%
              </p>
            </div>

            <!-- Préréglages rapides -->
            <div class="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 p-4 rounded-xl border border-blue-500/20">
              <h4 class="text-xs font-bold text-blue-400 uppercase mb-3">⚡ Préréglages rapides</h4>
              <div class="space-y-2">
                <button @click="applyPreset('classic')" class="w-full p-3 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg text-sm font-medium transition-all text-left">
                  🎯 Classique (Header + Appel + Historique + Footer)
                </button>
                <button @click="applyPreset('minimal')" class="w-full p-3 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg text-sm font-medium transition-all text-left">
                  ✨ Minimaliste (Appel uniquement)
                </button>
                <button @click="applyPreset('focused')" class="w-full p-3 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg text-sm font-medium transition-all text-left">
                  🎪 Focus (Header + Appel centré)
                </button>
                <button @click="applyPreset('vertical')" class="w-full p-3 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg text-sm font-medium transition-all text-left">
                  📱 Vertical (Appel au-dessus)
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- BACKGROUND SECTION -->
        <div v-if="activeSection === 'background'" class="space-y-6">
          <h3 class="text-lg font-black uppercase tracking-wide text-blue-400 mb-4">Arrière-plan</h3>
          
          <div class="space-y-4">
            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Type de fond</label>
              <select v-model="config.background.type" class="w-full p-3 bg-slate-800 border border-slate-700 rounded-xl text-sm font-medium focus:border-blue-500 outline-none transition-all">
                <option value="color">Couleur unie</option>
                <option value="gradient">Dégradé</option>
                <option value="image">Image</option>
              </select>
            </div>

            <div v-if="config.background.type === 'color'">
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Couleur</label>
              <input type="color" v-model="config.background.color" class="w-full h-12 rounded-xl cursor-pointer border-2 border-slate-700" />
            </div>

            <div v-if="config.background.type === 'gradient'" class="space-y-3">
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Couleur 1</label>
                  <input type="color" v-model="config.background.gradientColor1" class="w-full h-10 rounded-lg cursor-pointer" />
                </div>
                <div>
                  <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Couleur 2</label>
                  <input type="color" v-model="config.background.gradientColor2" class="w-full h-10 rounded-lg cursor-pointer" />
                </div>
              </div>
              <div>
                <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Direction</label>
                <select v-model="config.background.gradientDirection" class="w-full p-3 bg-slate-800 border border-slate-700 rounded-xl text-sm">
                  <option value="to right">Horizontal →</option>
                  <option value="to bottom">Vertical ↓</option>
                  <option value="135deg">Diagonal ↗</option>
                  <option value="to bottom right">Diagonal ↘</option>
                </select>
              </div>
            </div>

            <div v-if="config.background.type === 'image'">
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">URL de l'image</label>
              <input type="text" v-model="config.background.imageUrl" placeholder="https://..." class="w-full p-3 bg-slate-800 border border-slate-700 rounded-xl text-sm" />
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block mt-3">Ajustement</label>
              <select v-model="config.background.imageSize" class="w-full p-3 bg-slate-800 border border-slate-700 rounded-xl text-sm">
                <option value="cover">Couvrir</option>
                <option value="contain">Contenir</option>
              </select>
            </div>
          </div>
        </div>

        <!-- HEADER SECTION -->
        <div v-if="activeSection === 'header'" class="space-y-6">
          <h3 class="text-lg font-black uppercase tracking-wide text-blue-400 mb-4">En-tête</h3>
          
          <div class="space-y-4">
            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Texte du titre</label>
              <input type="text" v-model="config.header.title" class="w-full p-3 bg-slate-800 border border-slate-700 rounded-xl text-sm" />
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Taille titre</label>
                <input type="number" v-model.number="config.header.titleSize" class="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg text-sm" />
              </div>
              <div>
                <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Couleur</label>
                <input type="color" v-model="config.header.titleColor" class="w-full h-10 rounded-lg cursor-pointer" />
              </div>
            </div>

            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Police titre</label>
              <select v-model="config.header.titleFont" class="w-full p-3 bg-slate-800 border border-slate-700 rounded-xl text-sm">
                <option value="Plus Jakarta Sans">Plus Jakarta Sans</option>
                <option value="Inter">Inter</option>
                <option value="Outfit">Outfit</option>
                <option value="Space Grotesk">Space Grotesk</option>
                <option value="Orbitron">Orbitron</option>
              </select>
            </div>

            <div class="pt-4 border-t border-slate-800">
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Fond header</label>
              <input type="color" v-model="config.header.backgroundColor" class="w-full h-12 rounded-xl cursor-pointer" />
            </div>

            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Opacité</label>
              <input type="range" v-model.number="config.header.opacity" min="0" max="1" step="0.1" class="w-full" />
              <div class="text-xs text-slate-500 text-center mt-1">{{ Math.round(config.header.opacity * 100) }}%</div>
            </div>

            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Couleur bordure</label>
              <input type="color" v-model="config.header.borderColor" class="w-full h-10 rounded-lg cursor-pointer" />
            </div>
          </div>
        </div>

        <!-- CALLING SECTION -->
        <div v-if="activeSection === 'calling'" class="space-y-6">
          <h3 class="text-lg font-black uppercase tracking-wide text-blue-400 mb-4">Zone d'appel</h3>
          
          <div class="space-y-4">
            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Texte du label</label>
              <input type="text" v-model="config.calling.label" class="w-full p-3 bg-slate-800 border border-slate-700 rounded-xl text-sm" />
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Taille label</label>
                <input type="number" v-model.number="config.calling.labelSize" class="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg text-sm" />
              </div>
              <div>
                <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Couleur</label>
                <input type="color" v-model="config.calling.labelColor" class="w-full h-10 rounded-lg cursor-pointer" />
              </div>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Taille numéro</label>
                <input type="number" v-model.number="config.calling.numberSize" class="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg text-sm" />
              </div>
              <div>
                <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Couleur</label>
                <input type="color" v-model="config.calling.numberColor" class="w-full h-10 rounded-lg cursor-pointer" />
              </div>
            </div>

            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Police numéro</label>
              <select v-model="config.calling.numberFont" class="w-full p-3 bg-slate-800 border border-slate-700 rounded-xl text-sm">
                <option value="Plus Jakarta Sans">Plus Jakarta Sans</option>
                <option value="Inter">Inter</option>
                <option value="Outfit">Outfit</option>
                <option value="Orbitron">Orbitron</option>
              </select>
            </div>

            <div class="pt-4 border-t border-slate-800">
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Texte comptoir</label>
              <input type="text" v-model="config.calling.counterText" class="w-full p-3 bg-slate-800 border border-slate-700 rounded-xl text-sm" />
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Fond comptoir</label>
                <input type="color" v-model="config.calling.counterBg" class="w-full h-10 rounded-lg cursor-pointer" />
              </div>
              <div>
                <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Texte</label>
                <input type="color" v-model="config.calling.counterColor" class="w-full h-10 rounded-lg cursor-pointer" />
              </div>
            </div>

            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Arrondi comptoir</label>
              <input type="range" v-model.number="config.calling.counterRadius" min="0" max="50" class="w-full" />
              <div class="text-xs text-slate-500 text-center mt-1">{{ config.calling.counterRadius }}px</div>
            </div>

            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Fond zone</label>
              <input type="color" v-model="config.calling.backgroundColor" class="w-full h-12 rounded-xl cursor-pointer" />
            </div>

            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Opacité zone</label>
              <input type="range" v-model.number="config.calling.opacity" min="0" max="1" step="0.1" class="w-full" />
              <div class="text-xs text-slate-500 text-center mt-1">{{ Math.round(config.calling.opacity * 100) }}%</div>
            </div>
          </div>
        </div>

        <!-- HISTORY SECTION -->
        <div v-if="activeSection === 'history'" class="space-y-6">
          <h3 class="text-lg font-black uppercase tracking-wide text-blue-400 mb-4">Historique</h3>
          
          <div class="space-y-4">
            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Titre section</label>
              <input type="text" v-model="config.history.title" class="w-full p-3 bg-slate-800 border border-slate-700 rounded-xl text-sm" />
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Taille titre</label>
                <input type="number" v-model.number="config.history.titleSize" class="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg text-sm" />
              </div>
              <div>
                <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Couleur</label>
                <input type="color" v-model="config.history.titleColor" class="w-full h-10 rounded-lg cursor-pointer" />
              </div>
            </div>

            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Police titre</label>
              <select v-model="config.history.titleFont" class="w-full p-3 bg-slate-800 border border-slate-700 rounded-xl text-sm">
                <option value="Plus Jakarta Sans">Plus Jakarta Sans</option>
                <option value="Inter">Inter</option>
                <option value="Outfit">Outfit</option>
                <option value="Space Grotesk">Space Grotesk</option>
              </select>
            </div>

            <div class="pt-4 border-t border-slate-800">
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Fond items</label>
                  <input type="color" v-model="config.history.itemBg" class="w-full h-10 rounded-lg cursor-pointer" />
                </div>
                <div>
                  <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Bordure</label>
                  <input type="color" v-model="config.history.itemBorderColor" class="w-full h-10 rounded-lg cursor-pointer" />
                </div>
              </div>
            </div>

            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Arrondi items</label>
              <input type="range" v-model.number="config.history.itemRadius" min="0" max="40" class="w-full" />
              <div class="text-xs text-slate-500 text-center mt-1">{{ config.history.itemRadius }}px</div>
            </div>

            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Fond section</label>
              <input type="color" v-model="config.history.backgroundColor" class="w-full h-12 rounded-xl cursor-pointer" />
            </div>

            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Opacité</label>
              <input type="range" v-model.number="config.history.opacity" min="0" max="1" step="0.1" class="w-full" />
              <div class="text-xs text-slate-500 text-center mt-1">{{ Math.round(config.history.opacity * 100) }}%</div>
            </div>
          </div>
        </div>

        <!-- FOOTER SECTION -->
        <div v-if="activeSection === 'footer'" class="space-y-6">
          <h3 class="text-lg font-black uppercase tracking-wide text-blue-400 mb-4">Pied de page</h3>
          
          <div class="space-y-4">
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Taille labels</label>
                <input type="number" v-model.number="config.footer.labelSize" class="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg text-sm" />
              </div>
              <div>
                <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Couleur</label>
                <input type="color" v-model="config.footer.labelColor" class="w-full h-10 rounded-lg cursor-pointer" />
              </div>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Taille compteurs</label>
                <input type="number" v-model.number="config.footer.countSize" class="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg text-sm" />
              </div>
              <div>
                <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Couleur</label>
                <input type="color" v-model="config.footer.countColor" class="w-full h-10 rounded-lg cursor-pointer" />
              </div>
            </div>

            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Couleur points</label>
              <input type="color" v-model="config.footer.dotColor" class="w-full h-10 rounded-lg cursor-pointer" />
            </div>

            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Fond footer</label>
              <input type="color" v-model="config.footer.backgroundColor" class="w-full h-12 rounded-xl cursor-pointer" />
            </div>

            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Opacité</label>
              <input type="range" v-model.number="config.footer.opacity" min="0" max="1" step="0.1" class="w-full" />
              <div class="text-xs text-slate-500 text-center mt-1">{{ Math.round(config.footer.opacity * 100) }}%</div>
            </div>

            <div>
              <label class="text-xs font-bold text-slate-400 uppercase mb-2 block">Couleur bordure</label>
              <input type="color" v-model="config.footer.borderColor" class="w-full h-10 rounded-lg cursor-pointer" />
            </div>
          </div>
        </div>
      </div>
    </aside>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { toast } from "vue3-toastify";
import { useRoute } from 'vue-router';

const route = useRoute();
const agencyId = route.params.agencyId;

const sections = [
  { id: 'layout', name: 'Disposition', desc: 'Organisation & structure', icon: '📐' },
  { id: 'background', name: 'Arrière-plan', desc: 'Couleur, dégradé, image', icon: '🎨' },
  { id: 'header', name: 'En-tête', desc: 'Titre, horloge, couleurs', icon: '📋' },
  { id: 'calling', name: 'Zone d\'appel', desc: 'Numéro, comptoir, style', icon: '📢' },
  { id: 'history', name: 'Historique', desc: 'Liste des derniers tickets', icon: '📜' },
  { id: 'footer', name: 'Pied de page', desc: 'Statistiques des files', icon: '📊' }
];

const activeSection = ref('layout');

const config = ref({
  background: {
    type: 'gradient',
    color: '#0f172a',
    gradientColor1: '#0f172a',
    gradientColor2: '#1e293b',
    gradientDirection: 'to bottom right',
    imageUrl: '',
    imageSize: 'cover'
  },
  header: {
    title: 'MEDIALINK',
    titleSize: 32,
    titleColor: '#FFFFFF',
    titleFont: 'Plus Jakarta Sans',
    subtitleSize: 10,
    subtitleColor: '#3b82f6',
    subtitleFont: 'Plus Jakarta Sans',
    clockSize: 40,
    clockColor: '#FFFFFF',
    clockFont: 'Orbitron',
    backgroundColor: '#1e293b',
    opacity: 0.5,
    borderColor: '#334155'
  },
  calling: {
    label: 'CLIENT APPELÉ',
    labelSize: 16,
    labelColor: '#3b82f6',
    labelFont: 'Plus Jakarta Sans',
    numberSize: 120,
    numberColor: '#FFFFFF',
    numberFont: 'Orbitron',
    counterText: 'GUICHET 1',
    counterSize: 28,
    counterColor: '#FFFFFF',
    counterBg: '#3b82f6',
    counterFont: 'Plus Jakarta Sans',
    counterRadius: 24,
    backgroundColor: '#000000',
    opacity: 0.2
  },
  history: {
    title: 'HISTORIQUE',
    titleSize: 12,
    titleColor: '#64748b',
    titleFont: 'Plus Jakarta Sans',
    ticketSize: 28,
    ticketColor: '#FFFFFF',
    ticketFont: 'Orbitron',
    queueSize: 10,
    queueColor: '#3b82f6',
    queueFont: 'Plus Jakarta Sans',
    counterSize: 14,
    counterColor: '#cbd5e1',
    counterBg: '#334155',
    itemBg: '#1e293b66',
    itemBorderColor: '#33415580',
    itemRadius: 16,
    backgroundColor: '#000000',
    opacity: 0.2,
    borderColor: '#334155'
  },
  footer: {
    labelSize: 12,
    labelColor: '#64748b',
    labelFont: 'Plus Jakarta Sans',
    countSize: 20,
    countColor: '#FFFFFF',
    countFont: 'Orbitron',
    dotColor: '#22c55e',
    backgroundColor: '#0a0a0a',
    opacity: 1,
    borderColor: '#334155'
  },
  layout: {
    showHeader: true,
    showClock: true,
    showCalling: true,
    showHistory: true,
    showFooter: true,
    orientation: 'horizontal', // 'horizontal' ou 'vertical'
    callingWidth: 67, // Pour layout horizontal (%)
    callingHeight: 60, // Pour layout vertical (%)
    headerPosition: 'top', // 'top' ou 'bottom'
    headerHeight: 80, // px
    footerHeight: 80, // px
    callingPosition: 'left' // 'left', 'right', 'top', 'bottom'
  }
});

const getScreenBackgroundStyle = () => {
  const bg = config.value.background;
  
  if (bg.type === 'gradient') {
    return {
      background: `linear-gradient(${bg.gradientDirection}, ${bg.gradientColor1}, ${bg.gradientColor2})`
    };
  } else if (bg.type === 'image' && bg.imageUrl) {
    return {
      backgroundImage: `url(${bg.imageUrl})`,
      backgroundSize: bg.imageSize,
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    };
  }
  
  return {
    backgroundColor: bg.color
  };
};

const applyPreset = (presetName) => {
  const presets = {
    classic: {
      showHeader: true,
      showClock: true,
      showCalling: true,
      showHistory: true,
      showFooter: true,
      orientation: 'horizontal',
      callingWidth: 67,
      headerPosition: 'top',
      headerHeight: 80,
      footerHeight: 80,
      callingPosition: 'left'
    },
    minimal: {
      showHeader: false,
      showClock: false,
      showCalling: true,
      showHistory: false,
      showFooter: false,
      orientation: 'horizontal',
      callingWidth: 100,
      headerPosition: 'top',
      headerHeight: 80,
      footerHeight: 80,
      callingPosition: 'left'
    },
    focused: {
      showHeader: true,
      showClock: true,
      showCalling: true,
      showHistory: false,
      showFooter: false,
      orientation: 'horizontal',
      callingWidth: 100,
      headerPosition: 'top',
      headerHeight: 80,
      footerHeight: 80,
      callingPosition: 'left'
    },
    vertical: {
      showHeader: true,
      showClock: true,
      showCalling: true,
      showHistory: true,
      showFooter: true,
      orientation: 'vertical',
      callingHeight: 60,
      headerPosition: 'top',
      headerHeight: 80,
      footerHeight: 80,
      callingPosition: 'top'
    }
  };

  if (presets[presetName]) {
    config.value.layout = { ...config.value.layout, ...presets[presetName] };
    toast.success(`✨ Préréglage "${presetName}" appliqué !`);
  }
};

const fetchScreenConfig = async () => {
  try {
    const token = localStorage.getItem("superAdminToken");
    const res = await fetch(`https://api.datafuse.fr/api/super-admin/screen-config/${agencyId}`, {
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
    });
    
    if (res.status === 404) {
      toast.info("Configuration par défaut chargée");
      return;
    }
    
    const data = await res.json();
    if (data.config) {
      config.value = { ...config.value, ...data.config };
    }
  } catch (err) {
    toast.error("Erreur de chargement : " + err.message);
  }
};

const saveConfig = async () => {
  const idToast = toast.loading("Enregistrement...");
  try {
    const token = localStorage.getItem("superAdminToken");
    const res = await fetch(`https://api.datafuse.fr/api/super-admin/screen-config/${agencyId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ config: config.value })
    });
    
    if (!res.ok) throw new Error("Erreur lors de la sauvegarde");
    
    toast.update(idToast, { 
      render: "✨ Configuration publiée avec succès !", 
      type: "success", 
      isLoading: false, 
      autoClose: 3000 
    });
  } catch (err) {
    toast.update(idToast, { 
      render: err.message, 
      type: "error", 
      isLoading: false, 
      autoClose: 3000 
    });
  }
};

const resetToDefault = () => {
  if (confirm("Voulez-vous vraiment réinitialiser toute la configuration ?")) {
    window.location.reload();
  }
};

const callingSize = computed({
  get() {
    return config.value.layout.orientation === 'horizontal'
      ? config.value.layout.callingWidth
      : config.value.layout.callingHeight
  },
  set(value) {
    if (config.value.layout.orientation === 'horizontal') {
      config.value.layout.callingWidth = value
    } else {
      config.value.layout.callingHeight = value
    }
  }
})


onMounted(() => {
  fetchScreenConfig();
});
</script>

<style scoped>
.font-sans { font-family: 'Plus Jakarta Sans', sans-serif; }

@keyframes scroll-slow {
  0% { transform: translateX(100%); }
  100% { transform: translateX(-100%); }
}

.animate-scroll-slow {
  animation: scroll-slow 30s linear infinite;
}

::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.3);
}

::-webkit-scrollbar-thumb {
  background: rgba(100, 116, 139, 0.5);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(100, 116, 139, 0.7);
}
</style>